export class Bound {
    constructor(start, end) {
        if (start > end) {
            throw new RangeError("The 'start' argument must be <= the 'end' argument");
        }
        if (isNaN(start) || isNaN(end)) {
            throw new TypeError("Arguments must be numbers");
        }

        this.start = +start;
        this.end = +end;
    }

    get size() {
        return this.end - this.start;
    }

    /**
     * Shift start and end by given number.
     */
    shift(num) {
        return new Bound(this.start + num, this.end + num);
    }

    /**
     * Shifts the Bound so that its start is at zero.
     */
    toOrigin() {
        return this.shift(-this.start);
    }

    copy() {
        return new Bound(this.start, this.end);
    }

    difference(other) {
        if (other.start >= this.end || this.start >= other.end) {
            // disjoint
            return [this.copy()];
        }

        let result = [];
        if (this.start < other.start) {
            // diff section at start
            result.push(new Bound(this.start, other.start));
        }
        if (this.end > other.end) {
            // diff section at end
            result.push(new Bound(other.end, this.end));
        }

        return result;
    }

    intersection(other) {
        if (other.start >= this.end || this.start >= other.end) {
            return [];
        }
        return [new Bound(Math.max(this.start, other.start), Math.min(this.end, other.end))];
    }

    union(other) {
        if (other.start > this.end) {
            return [this.copy(), other.copy()];
        }
        if (this.start > other.end) {
            return [other.copy(), this.copy()];
        }

        return [new Bound(Math.min(this.start, other.start), Math.max(this.end, other.end))];
    }
}

export class BoundSet {
    constructor(...bounds) {
        this.bounds = this._toBoundArray(bounds);
        this._merge();
    }

    /**
     * Makes BoundSet iterable. Resulting iterator is that of the object's bounds Array.
     */
    [Symbol.iterator]() {
        return this.bounds[Symbol.iterator]();
    }

    /**
     * The length property of the object defined as the length of the bounds Array.
     */
    get length() {
        return this.bounds.length;
    }

    /**
     * The size property of the object. Defined as the sum of all the sizes of the Bound objects with in this.bounds.
     */
    get size() {
        return this.bounds.reduce((accumulator, currentValue) => {
            accumulator + currentValue.size;
        }, 0);
    }

    /**
     * Sort this.bounds on each Bounds object's start attribute.
     */
    _sort() {
        this.bounds.sort((a, b) => a.start - b.start);
    }

    /**
     * Sort and merge all overlapping bounds in the set.
     */
    _merge() {
        // make sure bounds are sorted before merging
        this._sort();

        this.bounds = this.bounds.reduce((accumulator, currentValue) => {
            if (accumulator.length == 0) {
                // push on initial value
                accumulator.push(currentValue);
            } else {
                // merge last and current if they overlap
                accumulator.push(...accumulator.pop().union(currentValue));
            }

            return accumulator;
        }, []);
    }

    _toBoundArray(args) {
        let bounds = [];
        let boundSets = [];
        for (const item of args) {
            if (item instanceof Bound) {
                bounds.push(item);
            } else if (item instanceof BoundSet) {
                boundSets.push(item);
            } else if (item instanceof Array) {
                bounds.push(new Bound(...item));
            } else {
                throw new TypeError(
                    `Argument is of type '${typeof item}'. Arguments must be of type Bound, BoundSet, or a 2 length Array of numbers`
                );
            }
        }

        let returned = boundSets.reduce((accumulator, currentValue) => {
            accumulator.push(...currentValue.bounds);
            return accumulator;
        }, bounds);

        return returned;
    }

    _normalizeOther(other) {
        if (other.length == 1 && other[0] instanceof BoundSet) {
            return other[0];
        }

        return new BoundSet(...this._toBoundArray(other));
    }

    /**
     * Make deep copy of this object.
     */
    copy() {
        return new BoundSet(...JSON.parse(JSON.stringify(this.bounds)));
    }

    /**
     * Determine if two BoundSets are equivalent.
     */
    equals(...other) {
        let normalized = this._normalizeOther(other);
        return this.symmetricDifference(normalized).isNullSet();
    }

    /**
     * Shift all bounds by num.
     */
    shift(num) {
        return new BoundSet(
            ...this.bounds.map((bound) => new Bound(bound.start + num, bound.end + num))
        );
    }

    /**
     * Shifts the BoundSet so that its global starting index is at zero.
     */
    toOrigin() {
        let shift = -this.bounds[0].start;
        return this.shift(shift);
    }

    /**
     * Get the set difference of two BoundSets.
     *
     * (***(   )   )
     */
    difference(...other) {
        let normalized = this._normalizeOther(other);
        return new BoundSet(
            ...normalized.bounds.reduce((accumulator, currentValue) => {
                return accumulator.reduce((a, c) => {
                    a.push(...c.difference(currentValue));
                    return a;
                }, []);
            }, this.bounds)
        );
    }

    /**
     * Get the set intersection of two BoundSets.
     *
     * (   (***)   )
     */
    intersection(...other) {
        let normalized = this._normalizeOther(other);
        let sections = [];
        for (const b1 of this.bounds) {
            for (const b2 of normalized.bounds) {
                sections.push(...b1.intersection(b2));
            }
        }

        return new BoundSet(...sections);
    }

    /**
     * Get the set symmetric difference  of two BoundSets.
     *
     * (***(  )***)
     */
    symmetricDifference(...other) {
        let normalized = this._normalizeOther(other);
        return this.difference(normalized).union(normalized.difference(this));
    }

    /**
     * Get the set union of two BoundSets.
     *
     * (***(***)***)
     */
    union(...other) {
        let normalized = this._toBoundArray(other);
        return new BoundSet(...this.bounds, ...normalized);
    }

    /**
     * Determine if other is disjoint from this BoundSet.
     *
     * (AAA) (BBB)      -> true
     * (AAA(ABAB)BBB)   -> false
     */
    isDisjointFrom(...other) {
        let normalized = this._normalizeOther(other);
        return this.intersection(normalized).isNullSet();
    }

    /**
     * Determine if this is a subset of other.
     *
     * (BBB(AAA)BBB)    -> true
     * (BBB(ABAB)AAA)   -> false
     */
    isSubsetOf(...other) {
        let normalized = this._normalizeOther(other);
        return this.difference(normalized).isNullSet();
    }

    /**
     * Determine if this is a superset of other.
     *
     * (AAA(BBB)AAA)    -> true
     * (AAA(ABAB)BBB)   -> false
     */
    isSupersetOf(...other) {
        let normalized = this._normalizeOther(other);
        return normalized.difference(this).isNullSet();
    }

    /**
     * Determine if this is the null set.
     */
    isNullSet() {
        return this.length == 0;
    }
}
